import { Injectable, OnDestroy, inject } from '@angular/core';
import { HttpHeaders, HttpClient, HttpErrorResponse } from '@angular/common/http';

import { Store } from '@ngrx/store';
import {
  BehaviorSubject,
  Observable,
  Subscription,
  throwError,
  map,
  take,
  switchMap,
  catchError,
  distinctUntilChanged,
  withLatestFrom,
  tap,
  Subject,
} from 'rxjs';

import { AppState, ConfigService, mapErrorForSentry, SentryLogger, unsubscribe } from '@fcom/core';
import { Profile } from '@fcom/core-api/login';
import { profile, loginToken, accountSecurityMfa } from '@fcom/core/selectors';
import { finShare, snapshot } from '@fcom/rx';
import { LoginActions } from '@fcom/core/actions';
import {
  AccountSecurityActionName,
  AccountSecurityError,
  MfaProvider,
} from '@fcom/core-api/login/interfaces/account-security.interface';

import { EmailConfirmErrorCode, EmailChangeError, EmailChangeErrorCode } from '../interfaces';

export enum TwoFactorAuthStatus {
  INITIAL = 'INITIAL',
  DISABLED = 'DISABLED',
  ENABLED = 'ENABLED',
  VERIFICATION_CODE_GENERATED = 'VERIFICATION_CODE_GENERATED',
  ERROR = 'ERROR',
}

export interface EnableGAuthValidatedResponse {
  secretKey: string;
}

export enum TwoFactorAuthType {
  EMAIL = 'EMAIL',
  SMS = 'SMS',
  APP = 'APP',
}

export interface TwoFactorAuthEnabledStatusData {
  isLoading: boolean;
  status: TwoFactorAuthStatus;
  isEnabled: boolean;
  isInitial: boolean;
  type?: TwoFactorAuthType;
}

export type UpdateEnabledStatus = Partial<Omit<TwoFactorAuthEnabledStatusData, 'isEnabled' | 'isInitial'>>;

export interface GetGAuthStatusResponse {
  secretKey?: string;
  validationCode?: number;
  scratchCodes?: number[];
  smsAuthenticator?: boolean;
  validated?: boolean;
  emailAuthenticator?: boolean;
}

export interface ValidateGAuthResponse {
  valid: boolean;
}

export interface Validate2faEmailVerificationCodeRequest {
  token: string;
  forceSetEmailMfa?: boolean;
}

export interface Validate2faEmailVerificationCodeResponse {
  emailMfaSetToEnabled: boolean;
  memberEmailChangedTo?: string;
}

export interface Validate2faEmailVerificationCodeErrorResponseGeneric {
  message?: string;
}

export type Validate2faEmailVerificationCodeErrorResponseWithCode =
  Validate2faEmailVerificationCodeErrorResponseGeneric & {
    message?: string;
    error: {
      code: EmailConfirmErrorCode;
    };
  };

export type Validate2faEmailVerificationCodeErrorResponse =
  | Validate2faEmailVerificationCodeErrorResponseGeneric
  | Validate2faEmailVerificationCodeErrorResponseWithCode;

export function isValidate2faEmailVerificationCodeErrorResponseWithCode(
  error: Validate2faEmailVerificationCodeErrorResponse
): error is Validate2faEmailVerificationCodeErrorResponseWithCode {
  return !!(error as Validate2faEmailVerificationCodeErrorResponseWithCode)?.error?.code;
}

const STATUS_PATH = 'getgauth';
const ENABLE_SMS_PATH = 'enablesmsauth';
const ENABLE_VALIDATED_PATH = 'enablegauthvalidated';
const VALIDATE_PATH = 'validategauth';
const DISABLE_PATH = 'disablegauth';

@Injectable({ providedIn: 'root' })
export class TwoFactorAuthService implements OnDestroy {
  private http = inject(HttpClient);
  private configService = inject(ConfigService);
  private store$ = inject<Store<AppState>>(Store);
  private sentryLogger = inject(SentryLogger);

  private subscriptions = new Subscription();
  private getStatusRequestSubscription: Subscription;
  private enableSMSAuthRequestSubscription: Subscription;
  private validateGAuthRequestSubscription: Subscription;
  private validate2faEmailVerificationCodeRequestSubscription: Subscription;
  private enableEmailMfaRequestSubscription: Subscription;
  private disableGAuthRequestSubscription: Subscription;

  private profile$: Observable<Profile> = this.store$.pipe(profile(), finShare());

  private enabledStatusState$ = new BehaviorSubject<TwoFactorAuthEnabledStatusData>({
    isLoading: false,
    status: TwoFactorAuthStatus.INITIAL,
    isEnabled: false,
    isInitial: true,
  });
  private updateEnabledStatus$ = new Subject<UpdateEnabledStatus>();

  enabledStatus$ = this.enabledStatusState$.pipe(finShare());

  private readonly mfaSwitchSuccess$ = new Subject<{ toType: TwoFactorAuthType }>();
  readonly mfaSwitchSuccessEvent$ = this.mfaSwitchSuccess$.pipe(finShare());

  constructor() {
    this.subscriptions.add(
      this.updateEnabledStatus$
        .pipe(
          withLatestFrom(this.enabledStatusState$),
          map(([update, last]) => {
            return {
              ...last,
              ...update,
              isEnabled:
                update.status === TwoFactorAuthStatus.ENABLED || update.status === TwoFactorAuthStatus.DISABLED
                  ? update.status === TwoFactorAuthStatus.ENABLED
                  : last.status === TwoFactorAuthStatus.ENABLED,
              isInitial:
                update.status === TwoFactorAuthStatus.ENABLED || update.status === TwoFactorAuthStatus.DISABLED
                  ? false
                  : last.isInitial,
              type:
                update.status === TwoFactorAuthStatus.INITIAL || update.status === TwoFactorAuthStatus.DISABLED
                  ? undefined
                  : update.type || last.type,
            };
          }),
          distinctUntilChanged(),
          withLatestFrom(this.store$.pipe(accountSecurityMfa()))
        )
        .subscribe({
          next: ([newEnabledStatusData, mfa]) => {
            let authTypeToMfaProvider: MfaProvider;

            switch (newEnabledStatusData.type) {
              case TwoFactorAuthType.EMAIL:
                authTypeToMfaProvider = MfaProvider.EmailAuth;
                break;
              case TwoFactorAuthType.APP:
                authTypeToMfaProvider = MfaProvider.MfaGauth;
                break;
              case TwoFactorAuthType.SMS:
                authTypeToMfaProvider = MfaProvider.SmsAuth;
                break;
              default:
                authTypeToMfaProvider = MfaProvider.None;
            }

            this.enabledStatusState$.next(newEnabledStatusData);

            if (
              mfa.isEnabled !== newEnabledStatusData.isEnabled ||
              mfa.provider !== authTypeToMfaProvider ||
              mfa.isInitial !== newEnabledStatusData.isInitial
            ) {
              this.store$.dispatch(
                LoginActions.setAccountSecurityStandaloneAction({
                  standaloneAction: {
                    name: AccountSecurityActionName.GetMfaStatus,
                    isLoading: false,
                    successData: { mfaProvider: authTypeToMfaProvider },
                  },
                })
              );
            }
          },
        })
    );

    this.subscriptions.add(
      this.store$.pipe(accountSecurityMfa(), withLatestFrom(this.enabledStatus$)).subscribe({
        next: ([mfa, enabledStatus]) => {
          let mfaProviderToAuthType: TwoFactorAuthType | undefined;

          switch (mfa.provider) {
            case MfaProvider.EmailAuth:
              mfaProviderToAuthType = TwoFactorAuthType.EMAIL;
              break;
            case MfaProvider.MfaGauth:
              mfaProviderToAuthType = TwoFactorAuthType.APP;
              break;
            case MfaProvider.SmsAuth:
              mfaProviderToAuthType = TwoFactorAuthType.SMS;
              break;
            default:
              mfaProviderToAuthType = undefined;
          }

          if (
            mfa.isEnabled !== enabledStatus.isEnabled ||
            mfaProviderToAuthType !== enabledStatus.type ||
            mfa.isInitial !== enabledStatus.isInitial ||
            mfa.getStatusError !== (enabledStatus.status === TwoFactorAuthStatus.ERROR)
          ) {
            this.updateEnabledStatus$.next({
              status: mfa.getStatusError
                ? TwoFactorAuthStatus.ERROR
                : mfa.isInitial
                  ? TwoFactorAuthStatus.INITIAL
                  : mfa.isEnabled
                    ? TwoFactorAuthStatus.ENABLED
                    : TwoFactorAuthStatus.DISABLED,
              isLoading: false,
              type: mfaProviderToAuthType,
            });
          }
        },
      })
    );
  }

  ngOnDestroy(): void {
    unsubscribe(this.subscriptions);
    this.getStatusRequestSubscription?.unsubscribe();
    this.enableSMSAuthRequestSubscription?.unsubscribe();
    this.validateGAuthRequestSubscription?.unsubscribe();
    this.validate2faEmailVerificationCodeRequestSubscription?.unsubscribe();
    this.enableEmailMfaRequestSubscription?.unsubscribe();
    this.disableGAuthRequestSubscription?.unsubscribe();
  }

  getStatus(): Observable<GetGAuthStatusResponse> {
    this.getStatusRequestSubscription?.unsubscribe();
    this.updateEnabledStatus$.next({ isLoading: true });

    const getStatusRequest$ = this.gAuthRequest<GetGAuthStatusResponse>(STATUS_PATH).pipe(finShare());

    this.getStatusRequestSubscription = getStatusRequest$.subscribe({
      next: (response) => {
        const appAuthenticator = response.secretKey && response.validated !== false;
        if (response.emailAuthenticator || response.smsAuthenticator || appAuthenticator) {
          this.updateEnabledStatus$.next({
            isLoading: false,
            status: TwoFactorAuthStatus.ENABLED,
            type: appAuthenticator
              ? TwoFactorAuthType.APP
              : response.emailAuthenticator
                ? TwoFactorAuthType.EMAIL
                : TwoFactorAuthType.SMS,
          });
        } else {
          this.updateEnabledStatus$.next({ isLoading: false, status: TwoFactorAuthStatus.DISABLED });
        }
      },
      error: (error: unknown) => {
        this.updateEnabledStatus$.next({ isLoading: false, status: TwoFactorAuthStatus.ERROR });

        this.store$.dispatch(
          LoginActions.setAccountSecurityStandaloneAction({
            standaloneAction: {
              name: AccountSecurityActionName.GetMfaStatus,
              isLoading: false,
              error: AccountSecurityError.Unknown,
            },
          })
        );

        this.sentryLogger.error('Error getting MFA status', {
          error: mapErrorForSentry(error),
        });
      },
    });

    return getStatusRequest$.pipe(take(1));
  }

  enableSMSAuth(): Observable<unknown> {
    this.enableSMSAuthRequestSubscription?.unsubscribe();
    this.updateEnabledStatus$.next({ isLoading: true });

    const enableSMSAuthRequest$ = this.gAuthRequest<unknown>(ENABLE_SMS_PATH).pipe(finShare());

    this.subscriptions.add(
      enableSMSAuthRequest$.subscribe({
        next: () => {
          this.updateEnabledStatus$.next({
            isLoading: false,
            status: TwoFactorAuthStatus.ENABLED,
            type: TwoFactorAuthType.SMS,
          });
        },
        error: (error: unknown) => {
          this.updateEnabledStatus$.next({ isLoading: false, status: TwoFactorAuthStatus.ERROR });

          this.sentryLogger.error('Error enabling SMS MFA', {
            error: mapErrorForSentry(error),
          });
        },
      })
    );

    return enableSMSAuthRequest$.pipe(take(1));
  }

  enableGAuthValidated(): Observable<EnableGAuthValidatedResponse> {
    this.updateEnabledStatus$.next({ isLoading: true });

    return this.gAuthRequest<EnableGAuthValidatedResponse>(ENABLE_VALIDATED_PATH).pipe(
      tap(() => {
        this.updateEnabledStatus$.next({ isLoading: false, status: TwoFactorAuthStatus.VERIFICATION_CODE_GENERATED });
      }),
      catchError((error: unknown) => {
        this.updateEnabledStatus$.next({ isLoading: false, status: TwoFactorAuthStatus.ERROR });

        this.sentryLogger.error('Error enabling GAuth Validated', {
          error: mapErrorForSentry(error),
        });

        return throwError(() => error);
      })
    );
  }

  getQRCodeImageUrl(secretKey: string): Observable<string> {
    return this.profile$.pipe(
      map(
        ({ memberNumber }) =>
          `https://${this.configService.cfg.casHost}/cas/gauth/qrgen?key=otpauth://totp/www.finnair.com${
            this.configService.cfg.casEnv === 'preprod' ? '(preprod)' : ''
          }:${memberNumber}?secret=${secretKey}&issuer=Finnair`
      )
    );
  }

  validateGAuth(verificationCode: string): Observable<ValidateGAuthResponse> {
    this.validateGAuthRequestSubscription?.unsubscribe();
    this.updateEnabledStatus$.next({ isLoading: true });

    const validateGAuthRequest$ = this.gAuthRequest<ValidateGAuthResponse>(`${VALIDATE_PATH}/${verificationCode}`).pipe(
      finShare()
    );

    this.validateGAuthRequestSubscription = validateGAuthRequest$.subscribe({
      next: (response) => {
        if (response.valid) {
          this.updateEnabledStatus$.next({
            isLoading: false,
            status: TwoFactorAuthStatus.ENABLED,
            type: TwoFactorAuthType.APP,
          });
          this.mfaSwitchSuccess$.next({ toType: TwoFactorAuthType.APP });
        } else {
          this.updateEnabledStatus$.next({ isLoading: false, status: TwoFactorAuthStatus.ERROR });
        }
      },
      error: (error: unknown) => {
        this.updateEnabledStatus$.next({ isLoading: false, status: TwoFactorAuthStatus.ERROR });
        this.sentryLogger.error(
          'Error in the process of enabling app MFA - validating the 2fa app verification code has failed',
          {
            error: mapErrorForSentry(error),
          }
        );
      },
    });

    return validateGAuthRequest$.pipe(take(1));
  }

  send2faEmailVerificationCode(): Observable<unknown> {
    this.updateEnabledStatus$.next({ isLoading: true });

    const apiUrl = `${this.configService.cfg.loyaltyServices.legacy.loyaltyProfileEmailVerifyUrl}`;

    const httpOptions = {
      headers: this.getLoyaltyServicesHttpHeaders(),
    };

    return this.http.post<void>(apiUrl, null, httpOptions).pipe(
      tap(() => {
        this.updateEnabledStatus$.next({
          isLoading: false,
          status: TwoFactorAuthStatus.VERIFICATION_CODE_GENERATED,
        });
      }),
      catchError((error: unknown) => {
        this.updateEnabledStatus$.next({ isLoading: false, status: TwoFactorAuthStatus.ERROR });

        this.sentryLogger.error(
          'Error in the process of enabling email MFA - sending 2fa email verification code has failed',
          {
            error: mapErrorForSentry(error),
          }
        );

        return throwError(() => error);
      })
    );
  }

  initiateEmailChange(newEmail: string): Observable<undefined> {
    this.updateEnabledStatus$.next({ isLoading: true });

    const apiUrl = `${this.configService.cfg.loyaltyServices.legacy.loyaltyProfileEmailChangeUrl}`;

    const httpOptions = {
      headers: this.getLoyaltyServicesHttpHeaders(),
    };

    return this.http.patch<undefined>(apiUrl, { newEmail }, httpOptions).pipe(
      tap(() => {
        this.updateEnabledStatus$.next({
          isLoading: false,
          status: TwoFactorAuthStatus.VERIFICATION_CODE_GENERATED,
        });
      }),
      catchError((error: unknown) => {
        this.updateEnabledStatus$.next({ isLoading: false, status: TwoFactorAuthStatus.ERROR });

        this.sentryLogger.error(
          'Error in the process of initiating email change - sending email change verification code has failed',
          {
            error: mapErrorForSentry(error),
          }
        );

        const status = (error as HttpErrorResponse)?.status;
        let errorCode: EmailChangeErrorCode;

        switch (status) {
          case 400:
            errorCode = EmailChangeErrorCode.INVALID_EMAIL;
            break;
          case 409:
            errorCode = EmailChangeErrorCode.SAME_EMAIL;
            break;
          case 503:
          default:
            errorCode = EmailChangeErrorCode.UNKNOWN;
        }

        return throwError(() => new EmailChangeError(errorCode));
      })
    );
  }

  validate2faEmailVerificationCode(
    verificationCode: string,
    /** If there is no newEmail and this is set to true, then we are in the enable email MFA flow, otherwise we are in the email verification flow. */
    forceSetEmailMfa: boolean,
    /** If this is set to a valid value, then we are in the change email flow and will be taken into account for error handling. */
    newEmail?: string
  ): Observable<Validate2faEmailVerificationCodeResponse> {
    this.validate2faEmailVerificationCodeRequestSubscription?.unsubscribe();
    this.updateEnabledStatus$.next({ isLoading: true });

    const apiUrl = `${this.configService.cfg.loyaltyServices.legacy.loyaltyProfileEmailConfirmUrl}`;
    const httpOptions = {
      headers: this.getLoyaltyServicesHttpHeaders(),
    };

    const validate2faEmailVerificationCodeRequest$ = this.http
      .post<Validate2faEmailVerificationCodeResponse>(
        apiUrl,
        {
          token: verificationCode,
          forceSetEmailMfa,
        } as Validate2faEmailVerificationCodeRequest,
        httpOptions
      )
      .pipe(finShare());

    this.validate2faEmailVerificationCodeRequestSubscription = validate2faEmailVerificationCodeRequest$
      .pipe(withLatestFrom(this.profile$))
      .subscribe({
        next: ([response, profile]) => {
          const newProfile: Profile = {
            ...profile,
            email: response.memberEmailChangedTo || profile.email,
            emailVerified: true,
          };
          this.store$.dispatch(LoginActions.setProfile({ profile: newProfile }));

          if (response.emailMfaSetToEnabled) {
            this.updateEnabledStatus$.next({
              isLoading: false,
              status: TwoFactorAuthStatus.ENABLED,
              type: TwoFactorAuthType.EMAIL,
            });
            this.mfaSwitchSuccess$.next({ toType: TwoFactorAuthType.EMAIL });
          } else {
            this.updateEnabledStatus$.next({ isLoading: false });
          }
        },
        error: (error: unknown) => {
          this.updateEnabledStatus$.next({ isLoading: false, status: TwoFactorAuthStatus.ERROR });

          const errorMessage = newEmail
            ? 'failed to change email'
            : forceSetEmailMfa
              ? 'failed to enable email MFA'
              : 'failed to verify email';

          this.sentryLogger.error(`Error in the process of validating the verification code - ${errorMessage}`, {
            error: mapErrorForSentry(error),
          });
        },
      });

    return validate2faEmailVerificationCodeRequest$.pipe(take(1));
  }

  enableEmailMfa(): Observable<undefined> {
    this.enableEmailMfaRequestSubscription?.unsubscribe();
    this.updateEnabledStatus$.next({ isLoading: true });

    const apiUrl = `${this.configService.cfg.loyaltyServices.legacy.loyaltyProfileMfaEmailEnable}`;
    const httpOptions = {
      headers: this.getLoyaltyServicesHttpHeaders(),
    };

    const enableEmailMfaRequest$ = this.http.post<undefined>(apiUrl, undefined, httpOptions).pipe(finShare());

    this.subscriptions.add(
      enableEmailMfaRequest$.subscribe({
        next: () => {
          this.updateEnabledStatus$.next({
            isLoading: false,
            status: TwoFactorAuthStatus.ENABLED,
            type: TwoFactorAuthType.EMAIL,
          });
          this.mfaSwitchSuccess$.next({ toType: TwoFactorAuthType.EMAIL });
        },
        error: (error: unknown) => {
          this.updateEnabledStatus$.next({ isLoading: false, status: TwoFactorAuthStatus.ERROR });

          this.sentryLogger.error('Error enabling email MFA', {
            error: mapErrorForSentry(error),
          });
        },
      })
    );

    return enableEmailMfaRequest$.pipe(take(1));
  }

  disableGAuth(): Observable<unknown> {
    this.disableGAuthRequestSubscription?.unsubscribe();
    this.updateEnabledStatus$.next({ isLoading: true });

    const disableGAuthRequest$ = this.gAuthRequest<unknown>(DISABLE_PATH).pipe(finShare());

    this.subscriptions.add(
      disableGAuthRequest$.subscribe({
        next: () => {
          this.updateEnabledStatus$.next({ isLoading: false, status: TwoFactorAuthStatus.DISABLED });
        },
        error: (error: unknown) => {
          this.updateEnabledStatus$.next({ isLoading: false, status: TwoFactorAuthStatus.ERROR });

          this.sentryLogger.error('Error disabling GAuth', {
            error: mapErrorForSentry(error),
          });
        },
      })
    );

    return disableGAuthRequest$.pipe(take(1));
  }

  private gAuthRequest<ResponseType>(path: string): Observable<ResponseType> {
    const token = snapshot(this.store$.pipe(loginToken()));
    return this.profile$.pipe(
      take(1),
      switchMap(({ memberNumber }) => {
        const httpOptions = {
          headers: new HttpHeaders({
            oauth_token: token,
            'x-api-key': this.configService.cfg.loyaltyServices.memberService.gauthApiKey,
          }),
        };
        return this.http.get<ResponseType>(
          `${this.configService.cfg.loyaltyServices.memberService.gauthUrl}/${path}/${memberNumber}?_=${Date.now()}`,
          httpOptions
        );
      }),
      catchError((error: unknown) => {
        this.sentryLogger.warn('Error submitting GAuth request', { error: mapErrorForSentry(error) });
        return throwError(() => error);
      })
    );
  }

  private getLoyaltyServicesHttpHeaders(): HttpHeaders {
    const token = snapshot(this.store$.pipe(loginToken()));
    const apiKey = this.configService.cfg.loyaltyServices.legacy.apiKey;

    return new HttpHeaders({
      Authorization: `Bearer ${token}`,
      'x-api-key': apiKey,
    });
  }
}
